'use client';

import { useState, useRef, useEffect } from 'react';
import { useTranslation } from '@/lib/i18n/useTranslation';

export interface SupplierOption {
  id: string;
  name: string;
  code?: string;
}

interface SupplierComboboxProps {
  value: string;
  valueId: string;
  onChange: (id: string, name: string, code?: string) => void;
  options: SupplierOption[];
  onAddNew?: (name: string) => void;
  label?: string;
  placeholder?: string;
  required?: boolean;
}

export function SupplierCombobox({
  value,
  valueId,
  onChange,
  options,
  onAddNew,
  label,
  placeholder,
  required = false,
}: SupplierComboboxProps) {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [inputValue, setInputValue] = useState(value);
  const [filteredOptions, setFilteredOptions] = useState(options);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setInputValue(value);
  }, [value]);

  useEffect(() => {
    const filtered = options.filter((opt) =>
      opt.name.toLowerCase().includes(inputValue.toLowerCase())
    );
    setFilteredOptions(filtered);
  }, [inputValue, options]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        // If the input value doesn't match any option and is not empty, treat as new
        if (inputValue && !options.some(opt => opt.name.toLowerCase() === inputValue.toLowerCase())) {
          onChange('', inputValue);
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [inputValue, options, onChange]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setInputValue(newValue);
    setIsOpen(true);
  };

  const handleSelectOption = (option: SupplierOption) => {
    setInputValue(option.name);
    onChange(option.id, option.name, option.code);
    setIsOpen(false);
  };

  const handleAddNew = () => {
    if (inputValue.trim() && !options.some(opt => opt.name.toLowerCase() === inputValue.trim().toLowerCase())) {
      const newName = inputValue.trim();
      if (onAddNew) {
        onAddNew(newName);
      }
      onChange('', newName);
      setIsOpen(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (filteredOptions.length > 0) {
        handleSelectOption(filteredOptions[0]);
      } else if (inputValue.trim()) {
        handleAddNew();
      }
    } else if (e.key === 'Escape') {
      setIsOpen(false);
    }
  };

  const showAddNew = inputValue.trim() && !options.some(
    (opt) => opt.name.toLowerCase() === inputValue.toLowerCase()
  );

  return (
    <div className="relative" ref={containerRef}>
      {label && (
        <label className="block text-xs font-medium text-foreground-muted mb-1">
          {label}
          {required && <span className="text-danger ml-1">*</span>}
        </label>
      )}

      <div className="relative">
        <input
          ref={inputRef}
          type="text"
          value={inputValue}
          onChange={handleInputChange}
          onFocus={() => setIsOpen(true)}
          onKeyDown={handleKeyDown}
          placeholder={placeholder || t('inventory.selectSupplier')}
          className="w-full px-3 py-2 text-sm rounded-lg bg-card-bg border border-card-border text-foreground pr-8"
        />
        <button
          type="button"
          onClick={() => setIsOpen(!isOpen)}
          className="absolute right-2 top-1/2 -translate-y-1/2 text-foreground-muted hover:text-foreground"
        >
          <svg
            className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`}
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>
      </div>

      {isOpen && (
        <div className="absolute z-50 w-full mt-1 bg-card-bg border border-card-border rounded-lg shadow-lg max-h-60 overflow-auto">
          {filteredOptions.length > 0 ? (
            <ul>
              {filteredOptions.map((option) => (
                <li
                  key={option.id}
                  onClick={() => handleSelectOption(option)}
                  className={`px-3 py-2 cursor-pointer hover:bg-background-secondary text-sm ${
                    valueId === option.id ? 'bg-primary/10 text-primary' : 'text-foreground'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <span>{option.name}</span>
                    {option.code && (
                      <span className="text-xs text-foreground-muted">{option.code}</span>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          ) : (
            <p className="px-3 py-2 text-sm text-foreground-muted">
              {t('inventory.noMatchingSuppliers')}
            </p>
          )}

          {showAddNew && onAddNew && (
            <button
              onClick={handleAddNew}
              className="w-full px-3 py-2 text-sm text-left text-primary hover:bg-primary/10 border-t border-card-border flex items-center gap-2"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              {t('inventory.addNewSupplier')}: &quot;{inputValue}&quot;
            </button>
          )}
        </div>
      )}
    </div>
  );
}

